// src/components/store/sections/product-cards/GlassAuroraProductCard.tsx
"use client";

import Image from "next/image";
import Link from "next/link";
import { Star, ShoppingBag, Sparkles, Heart, Check } from "lucide-react";
import { UniversalProductCardProps, useCardActions } from "./useCardActions";

const AURORA_THEMES = [
  {
    orb1: "bg-fuchsia-500/30",
    orb2: "bg-cyan-400/30",
    btn: "from-fuchsia-600 via-purple-600 to-cyan-500",
    badge: "text-fuchsia-700 bg-fuchsia-500/10 border-fuchsia-300/40",
  },
  {
    orb1: "bg-emerald-400/30",
    orb2: "bg-sky-500/30",
    btn: "from-emerald-500 via-teal-500 to-sky-600",
    badge: "text-emerald-700 bg-emerald-500/10 border-emerald-300/40",
  },
  {
    orb1: "bg-amber-400/35",
    orb2: "bg-rose-500/30",
    btn: "from-amber-500 via-orange-500 to-rose-600",
    badge: "text-rose-700 bg-rose-500/10 border-rose-300/40",
  },
  {
    orb1: "bg-violet-500/35",
    orb2: "bg-indigo-400/30",
    btn: "from-violet-600 via-indigo-600 to-blue-600",
    badge: "text-violet-700 bg-violet-500/10 border-violet-300/40",
  },
];

export default function GlassAuroraProductCard({
  product,
  index = 0,
  subdomain = "nextlook",
}: UniversalProductCardProps) {
  const {
    productImg,
    productName,
    productUrl,
    categoryName,
    rating,
    currentPrice,
    comparePrice,
    added,
    wishlisted,
    setWishlisted,
    handleAddToCart,
  } = useCardActions(product, subdomain);

  const theme = AURORA_THEMES[index % AURORA_THEMES.length];

  return (
    <div className="relative w-full h-full rounded-[32px] p-4 bg-white/80 backdrop-blur-2xl border border-white/80 shadow-[0_12px_36px_rgba(31,38,135,0.08)] hover:shadow-[0_20px_48px_rgba(31,38,135,0.16)] overflow-hidden flex flex-col justify-between group transition-all duration-300 select-none">
      {/* Ambient Aurora Mesh Orbs */}
      <div className={`absolute -top-10 -left-10 w-36 h-36 rounded-full ${theme.orb1} blur-2xl pointer-events-none`} />
      <div className={`absolute bottom-10 -right-10 w-36 h-36 rounded-full ${theme.orb2} blur-2xl pointer-events-none`} />

      <div className="relative z-10">
        <div className="flex items-center justify-between gap-2 mb-2.5">
          <span className={`px-2.5 py-0.5 rounded-full text-[9px] font-black uppercase tracking-wider border backdrop-blur-md flex items-center gap-1 ${theme.badge}`}>
            <Sparkles className="w-2.5 h-2.5" /> {categoryName}
          </span>

          <button
            type="button"
            onClick={() => setWishlisted(!wishlisted)}
            className="w-7 h-7 rounded-full bg-white/70 backdrop-blur-md border border-slate-200/60 flex items-center justify-center text-slate-400 hover:text-rose-500 cursor-pointer"
          >
            <Heart className={`w-3.5 h-3.5 ${wishlisted ? "fill-rose-500 text-rose-500" : ""}`} />
          </button>
        </div>

        {/* Frosted Inner Glass Stage */}
        <Link
          href={productUrl}
          className="relative w-full h-44 rounded-2xl bg-white/60 backdrop-blur-md border border-white/90 shadow-inner flex items-center justify-center p-3 mb-3 block"
        >
          <div className="relative w-36 h-36">
            <Image
              src={productImg}
              alt={productName}
              fill
              unoptimized
              className="object-contain group-hover:scale-110 transition-transform duration-500 drop-shadow-xl"
            />
          </div>

          <div className="absolute bottom-2 right-2 px-2 py-0.5 rounded-full bg-slate-900/80 backdrop-blur-md text-white text-[10px] font-bold flex items-center gap-1">
            <Star className="w-2.5 h-2.5 fill-amber-400 text-amber-400" />
            <span>{rating.toFixed(1)}</span>
          </div>
        </Link>

        <Link href={productUrl} className="block">
          <h3 className="text-sm sm:text-base font-black text-slate-900 truncate hover:text-purple-600 transition-colors">
            {productName}
          </h3>
        </Link>

        <div className="flex items-baseline gap-2 mt-1 font-mono">
          <span className="text-lg sm:text-xl font-black text-slate-900">
            ৳{currentPrice.toLocaleString()}
          </span>
          {comparePrice > currentPrice && (
            <span className="text-xs text-slate-400 line-through">
              ৳{comparePrice.toLocaleString()}
            </span>
          )}
        </div>
      </div>

      <div className="relative z-10 pt-3 mt-2">
        <button
          type="button"
          onClick={(e) => handleAddToCart(e)}
          className={`w-full py-2.5 px-4 rounded-2xl bg-gradient-to-r ${theme.btn} text-white font-black text-[11px] uppercase tracking-wider shadow-lg hover:opacity-95 active:scale-95 transition-all cursor-pointer flex items-center justify-center gap-1.5`}
        >
          {added ? <Check className="w-3.5 h-3.5" /> : <ShoppingBag className="w-3.5 h-3.5" />}
          <span>{added ? "Added to Bag" : "Aurora Quick Add"}</span>
        </button>
      </div>
    </div>
  );
}
